import { shallowEqual } from "react-redux";

import type { RootState } from "~/app/store";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { ProductGroup } from "~/features/cart/cart/dto/CartResponseDTO";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import type { ProductDescriptorSelection } from "~/features/cart/cartV2/models/ProductTableViewModel";

export interface ResolvedProductGroup {
  productGroup: ProductGroup;
  productGroupNumber: number;
  lineItems: LineItem[];
  descriptors: ProductDescriptor[];
  descriptorsByPriceReference: Record<string, ProductDescriptor>;
  mainDescriptor: ProductDescriptor | null;
  mainDescriptorSelection: ProductDescriptorSelection;
}

export function selectDescriptorForPriceReference(
  descriptors: ProductDescriptor[],
  priceReference?: string | null,
): ProductDescriptorSelection {
  if (descriptors.length === 0) {
    return {
      descriptor: null,
      strategy: "missing",
      priceReference: priceReference ?? undefined,
    };
  }

  if (descriptors.length === 1) {
    return {
      descriptor: descriptors[0],
      strategy: "single-descriptor",
      priceReference: priceReference ?? descriptors[0].priceReference,
    };
  }

  if (priceReference != null) {
    const matchingDescriptor = descriptors.find((descriptor) => descriptor.priceReference === priceReference);

    if (matchingDescriptor) {
      return {
        descriptor: matchingDescriptor,
        strategy: "matched-price-reference",
        priceReference,
      };
    }
  }

  const loadedDescriptor = descriptors.find((descriptor) => descriptor.isLoaded && !descriptor.isError);
  if (loadedDescriptor) {
    return {
      descriptor: loadedDescriptor,
      strategy: "first-loaded",
      priceReference: loadedDescriptor.priceReference,
    };
  }

  const nonErrorDescriptor = descriptors.find((descriptor) => !descriptor.isError);
  if (nonErrorDescriptor) {
    return {
      descriptor: nonErrorDescriptor,
      strategy: "first-non-error",
      priceReference: nonErrorDescriptor.priceReference,
    };
  }

  return {
    descriptor: descriptors[0],
    strategy: "first-available",
    priceReference: descriptors[0].priceReference,
  };
}

export function selectMainDescriptor(
  descriptors: ProductDescriptor[],
  descriptorSourceEntries: Pick<LineItem, "priceReference">[],
): ProductDescriptorSelection {
  return selectDescriptorForPriceReference(descriptors, descriptorSourceEntries[0]?.priceReference);
}

export function selectDescriptorForLineItem(
  entry: Pick<ResolvedProductGroup, "descriptors" | "descriptorsByPriceReference">,
  lineItem: Pick<LineItem, "priceReference">,
): ProductDescriptorSelection {
  const { priceReference } = lineItem;

  if (priceReference != null) {
    const directDescriptor = entry.descriptorsByPriceReference[priceReference] ?? null;

    if (directDescriptor) {
      return {
        descriptor: directDescriptor,
        strategy: "matched-price-reference",
        priceReference,
      };
    }

    const matchingDescriptors = entry.descriptors.filter((descriptor) => descriptor.priceReference === priceReference);
    if (matchingDescriptors.length === 1) {
      return {
        descriptor: matchingDescriptors[0],
        strategy: "matched-price-reference",
        priceReference,
      };
    }
  }

  return selectDescriptorForPriceReference(entry.descriptors, priceReference);
}

function compareProductDescriptorSelectionEquality(
  left: ProductDescriptorSelection,
  right: ProductDescriptorSelection,
): boolean {
  return (
    left.descriptor === right.descriptor &&
    left.strategy === right.strategy &&
    left.priceReference === right.priceReference
  );
}

const resolvedGroupCache = new Map<
  number,
  { productGroup: ProductGroup; lineItems: LineItem[]; descriptors: ProductDescriptor[]; result: ResolvedProductGroup }
>();

export function evictStaleGroupsFromCache(activeGroupNumbers: Set<number>) {
  for (const groupNumber of resolvedGroupCache.keys()) {
    if (!activeGroupNumbers.has(groupNumber)) {
      resolvedGroupCache.delete(groupNumber);
    }
  }
}

export const selectResolvedProductGroup =
  (productGroup: ProductGroup, lineItemsByGroupNumber: Record<number, LineItem[]>) =>
  (state: RootState): ResolvedProductGroup => {
    const lineItems = lineItemsByGroupNumber[productGroup.groupNumber] ?? [];

    // Fast path: if productGroup and lineItems references are identical,
    // descriptors (derived from memoized selectProductDescriptor) are almost certainly
    // identical too — skip their computation entirely.
    const cached = resolvedGroupCache.get(productGroup.groupNumber);
    if (cached && cached.productGroup === productGroup && cached.lineItems === lineItems) {
      return cached.result;
    }

    const priceReferences = Array.from(new Set(lineItems.map((lineItem) => lineItem.priceReference ?? "")));
    const descriptors = priceReferences.map((priceReference) =>
      selectProductDescriptor(state, {
        productCode: productGroup.productCode,
        priceReference,
      }),
    );

    // Slow path: productGroup or lineItems changed — check if descriptors are still equal
    if (
      cached &&
      cached.productGroup === productGroup &&
      shallowEqual(cached.lineItems, lineItems) &&
      shallowEqual(cached.descriptors, descriptors)
    ) {
      return cached.result;
    }

    const descriptorsByPriceReference = descriptors.reduce<Record<string, ProductDescriptor>>((acc, descriptor) => {
      acc[descriptor.priceReference] = descriptor;
      return acc;
    }, {});

    const mainDescriptorSelection = selectMainDescriptor(descriptors, lineItems);

    const result: ResolvedProductGroup = {
      productGroup,
      productGroupNumber: productGroup.groupNumber,
      lineItems,
      descriptors,
      descriptorsByPriceReference,
      mainDescriptor: mainDescriptorSelection.descriptor,
      mainDescriptorSelection,
    };

    resolvedGroupCache.set(productGroup.groupNumber, { productGroup, lineItems, descriptors, result });
    return result;
  };

export function compareResolvedProductGroupEquality(left: ResolvedProductGroup, right: ResolvedProductGroup): boolean {
  if (left.productGroup !== right.productGroup) {
    return false;
  }
  if (!shallowEqual(left.descriptors, right.descriptors)) {
    return false;
  }
  if (!shallowEqual(left.descriptorsByPriceReference, right.descriptorsByPriceReference)) {
    return false;
  }
  if (left.mainDescriptor !== right.mainDescriptor) {
    return false;
  }
  if (!compareProductDescriptorSelectionEquality(left.mainDescriptorSelection, right.mainDescriptorSelection)) {
    return false;
  }
  if (left.lineItems.length !== right.lineItems.length) {
    return false;
  }
  return shallowEqual(left.lineItems, right.lineItems);
}
